import React, { useMemo, useRef, useEffect } from 'react';
import { DndProvider, XYCoord, useDragLayer } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import cm from 'classnames';

import { StoryBoardTracks } from 'services/storyboard.service';
import { useAppSelector, useAppDispatch } from 'store/store-hooks';
import { getSbPosition, getSbTracks } from 'components/app/_helpers/_app.helper';
import { EnumDragger, setDragger } from 'components/app/_mediator/app.slice';
import { selectHelpMode } from 'components/app/_mediator/app-data.slice';
import { useMousePosition } from 'components/app/_mediator/app-cursor-position.provider';
import { useClipThumbPrevContext } from 'components/app/_mediator/clip-thumb-prev.provider';
import { useClipThumbContext } from 'components/app/_mediator/clip-thumb/clip-thumb.provider';
import { EMPTY_CELL } from 'components/app/editor/_mediator/editor.slice';
import { SbPosition } from 'components/app/editor/_mediator/editor-controls.slice';
import { useMemory } from 'components/app/_mediator/memory/use-memory';
import { useMemorySb } from 'components/app/_mediator/memory/use-memory-sb';
import { useZoomSb } from 'components/app/_mediator/zoom/use-zoom-effects';
import { useZoomCb } from 'components/app/_mediator/zoom/use-zoom-cb';
import { IntroKeys } from 'components/app/intro/intro-keys';
import { useSb } from 'components/app/storyboard/_mediator/use-sb';
import { useSbCb } from 'components/app/storyboard/_mediator/use-sb-cb';
import { SbTracksMode, setView } from 'components/app/storyboard/_mediator/sb.slice';
import { SbDndProvider, useSbDndContext } from './sb-tracks-dnd/sb-dnd.provider';

import { ReactComponent as PlusIcon } from 'assets/ui/plus.svg';
import { ReactComponent as MinusIcon } from 'assets/ui/minus.svg';
import { Tooltip } from 'components/UI/tooltip/tooltip';
import { Track, TrackHeader, TrackBody, TrackFeed } from 'components/app/app-components/track-container/track-elements';
import { ClipThumbPrev } from 'components/app/app-components/clip-thumb-prev/clip-thumb-prev';
import { ClipErrorStatusBar } from 'components/app/app-components/clip-error-status-bar/clip-error-status-bar';
import { SbCell } from './sb-cell';
import styles from './_sb-tracks.module.scss';

export const SbTracks = () => {
  const { markers, cells, storyBoard } = useAppSelector((state) => state.editor);
  const storyLoaded = useAppSelector((state) => state.app.storyLoaded);
  const snapshot = useAppSelector((state) => state.memory.storyboard);
  const errorRateMap = useAppSelector((state) => state.sb.errorRateMap);
  const { keepCursorInView } = useZoomCb();
  const { sbSelectClip } = useSbCb();

  useSb();
  useMemory(snapshot, useMemorySb());

  const keys = useMemo(() => {
    if (!markers) return;
    return [0, ...markers];
  }, [cells?.duration]);

  const data = useMemo(() => {
    if (!keys || !cells) return;
    return { keys, cells, storyBoard };
  }, [keys, storyBoard]);

  const handleErrorClick = (clipId: ClipId) => {
    const position = getSbPosition(clipId, storyBoard);
    sbSelectClip(clipId, position.cell, position.track);
    keepCursorInView();
  };

  if (!storyLoaded || !data) return null;

  return (
    <SbDndProvider>
      <DndProvider backend={HTML5Backend}>
        {getSbTracks().map((trackId) => (
          <SbTrack key={trackId} trackId={trackId}>
            {data.keys.map((key, i) => (
              <SbCell
                key={String(key)}
                track={trackId}
                clipId={data.storyBoard[trackId][i]}
                width={data.cells.width[i]}
                index={i}
              />
            ))}
          </SbTrack>
        ))}
        <ClipDragLayer />
        <ClipThumbPrev />
        <ClipErrorStatusBar errorClips={Object.keys(errorRateMap)} handleClick={handleErrorClick} />
      </DndProvider>
    </SbDndProvider>
  );
};

/* C O M P O N E N T S */

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  trackId: StoryBoardTracks;
}

const SbTrack = ({ trackId, children, ...rest }: Props) => {
  const { isDragging } = useSbDndContext();
  const { resetHoveredClip } = useClipThumbPrevContext();
  const scrollSyncLock = useAppSelector((state) => state.sb.scrollSyncLock);
  const sbTrack = useAppSelector((state) => state.editor__controls.sbPosition.track);
  const storyBoard = useAppSelector((state) => state.editor.storyBoard);
  const sbViewMode = useAppSelector((state) => state.sb.sbViewMode);
  const heldKey = useAppSelector((state) => state.app.heldKey);
  const intro = useAppSelector((state) => state.app.intro);
  const helpMode = useAppSelector(selectHelpMode);
  const container = useRef<HTMLDivElement>(null);
  const feed = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();

  const isSelected = () => {
    return trackId === sbTrack;
  };

  const trackDescription = useMemo(() => {
    switch (trackId) {
      case StoryBoardTracks.storyBoard:
        return 'Arrange clips to tell your story - this track defines your montage to be rendered';
      case StoryBoardTracks.clipBoard:
        return 'Use this track to stage and swap clip sequences';
      default:
        return 'Use this track to discard clips you are not quite ready to delete yet';
    }
  }, []);

  const isExpanded = !sbViewMode.sbTracks[trackId];

  useZoomSb(container, feed, trackId);

  const trackClipsLength = useMemo(() => {
    return storyBoard[trackId].filter((clip) => clip).length;
  }, [storyBoard[trackId]]);

  const handleSbTrackHeaderClick = () => {
    const trackState = sbViewMode.sbTracks[trackId] === 'collapsed' ? null : 'collapsed';
    const sbTracks = { ...sbViewMode.sbTracks, [trackId]: trackState };

    if (heldKey === 'Control') {
      dispatch(
        setView({
          audio: sbViewMode.audio,
          sbTracks,
          prevSbTracks: sbTracks,
        })
      );

      return;
    }

    const tracksMap = Object.keys(sbViewMode.sbTracks);
    const tracksToToggle = tracksMap.filter((_, index) => index >= tracksMap.indexOf(trackId));
    const updatedSbTrackMode = { ...sbViewMode.sbTracks };

    tracksToToggle.forEach((trackID) => (updatedSbTrackMode[trackID as keyof SbTracksMode] = trackState));

    dispatch(
      setView({
        audio: sbViewMode.audio,
        sbTracks: updatedSbTrackMode,
        prevSbTracks: updatedSbTrackMode,
      })
    );
  };

  // Determine which data-intro to use based on tutorial state
  const getDataIntro = () => {
    if (trackId === StoryBoardTracks.storyBoard) {
      // When storyboard tutorial is running, highlight entire track; otherwise highlight only header
      return intro === IntroKeys.storyBoardHeader ? IntroKeys.storyBoardHeader : IntroKeys.storyBoard;
    }
    return IntroKeys[trackId];
  };

  return (
    <Track
      {...rest}
      className={cm(styles.SbTrack, {
        [styles['SbTrack--active']]: isSelected(),
        [styles['SbTrack--dndActive']]: isDragging,
        [styles['SbTrack--hideScroll']]: !scrollSyncLock && !isSelected(),
        [styles['SbTrack--collapsed']]: !isExpanded,
      })}
      data-intro={getDataIntro()}
    >
      <Tooltip
        tip={`${trackId} track: ${trackDescription} (click to ${isExpanded ? 'collapse' : 'expand'})`}
        showTooltip={helpMode}
      >
        <div className={styles.SbTrack__headerWrapper} onClick={handleSbTrackHeaderClick}>
          {isExpanded ? <MinusIcon className={styles.expandIcon} /> : <PlusIcon className={styles.expandIcon} />}
          <TrackHeader
            className={styles.SbTrack__header}
            data-intro={trackId === StoryBoardTracks.storyBoard ? IntroKeys.storyBoardHeader : IntroKeys[trackId]}
          >
            {`${trackId} (Clips: ${trackClipsLength})`}
          </TrackHeader>
        </div>
      </Tooltip>
      <TrackBody
        ref={container}
        className={cm(styles.SbTrack__body, { [styles['SbTrack__body--collapsed']]: !isExpanded })}
      >
        <TrackFeed ref={feed} onMouseLeave={resetHoveredClip}>
          {children}
        </TrackFeed>
      </TrackBody>
    </Track>
  );
};

const ClipDragLayer = () => {
  const storyBoard = useAppSelector((state) => state.editor.storyBoard);
  const clipThumbnails = useAppSelector((state) => state.clipThumb.clipThumbnails);
  const { getThumbnailSrc } = useClipThumbContext();
  const { isDragging, item, initialOffset, clientOffset } = useDragLayer((monitor) => ({
    item: monitor.getItem() as SbPosition,
    clientOffset: monitor.getClientOffset(),
    initialOffset: monitor.getInitialSourceClientOffset(),
    isDragging: monitor.isDragging(),
  }));
  const setPosition = useMousePosition();
  const dispatch = useAppDispatch();

  const clipId = item ? storyBoard[item.track]?.[item.cell] : null;

  const src = useMemo(() => {
    if (clipId === EMPTY_CELL) return '';
    return getThumbnailSrc(clipId, clipThumbnails);
  }, [clipId]);

  useEffect(() => {
    if (!clientOffset) return;
    setPosition({ x: clientOffset.x, y: clientOffset.y });
  }, [clientOffset]);

  useEffect(() => {
    dispatch(setDragger(isDragging ? EnumDragger.dndSbClip : null));
  }, [isDragging]);

  if (!isDragging || !clipId) return null;

  return (
    <div className={styles.DragLayer}>
      <div className={styles.DragLayer__clip} style={getDragLayerPosition(initialOffset, clientOffset)}>
        <img src={src} crossOrigin="anonymous" />
      </div>
    </div>
  );
};

/* H E L P E R */

const getDragLayerPosition = (initialOffset: XYCoord | null, clientOffset: XYCoord | null) => {
  if (!initialOffset || !clientOffset) return { display: 'none' };
  const { x, y } = clientOffset;
  const transform = `translate(${x}px, ${y}px)`;
  return {
    transform,
    WebkitTransform: transform,
  };
};
